'use client';

import { useStore } from '@/providers/app-store-provider';
import { useTranslations } from 'next-intl';
import React from 'react';
import { InView, IntersectionOptions } from 'react-intersection-observer';
import LoadingCircular from '../LoadingCircular';
interface InfiniteScrollProps extends IntersectionOptions {
  /**
   * 是否正在加载数据
   */
  loading?: boolean;

  /**
   * 当触底时调用的回调函数
   */
  onReachBottom?: () => void;

  /**
   * 加载中显示的文本
   */
  loadingText?: string;

  /**
   * 自定义加载状态的渲染
   */
  loadingRender?: React.ReactNode;

  /**
   * 外层容器的类名
   */
  className?: string;

  /**
   * 当前页码
   */
  page?: number;

  /**
   * 总页数
   */
  total?: number;

  /**
   * 每页条数
   */
  limit?: number;

  /**
   * 是否显示没有更多
   */
  showNoMore?: boolean;
}

/**
 * 触底加载更多组件
 * 当用户滚动到页面底部时，触发加载更多数据
 */
const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  loading = false,
  onReachBottom,
  loadingText,
  loadingRender,
  className = 'h-[64px] flex items-center justify-center',
  page = 1,
  total = 1,
  limit = 10,
  showNoMore = true,
  threshold,
  rootMargin,
}) => {
  const t = useTranslations('components.infiniteScroll');
  const waiMode = useStore((state) => state.waiMode);

  // 如果没有传入loadingText，使用翻译的默认值
  const actualLoadingText = loadingText || t('loading');

  const handleInView = (inView: boolean) => {
    // 如果视图可见、未加载中，调用回调
    if (inView && !loading && page < total / limit) {
      onReachBottom();
    }
  };

  return (
    <InView
      className={className}
      onChange={handleInView}
      threshold={threshold}
      rootMargin={rootMargin}
    >
      {loading && (loadingRender || <LoadingCircular loadingText={actualLoadingText} />)}
      {page * limit >= total && total > 0 && !loading && showNoMore && (
        <div className="flex items-center justify-center">
          <p className="w-40 h-1 bg-auxiliary-dark-gray" />
          <span
            className={`text-12 ${waiMode ? 'text-primary-black' : 'text-auxiliary-dark-gray'} mx-8`}
          >
            {t('noMoreText')}
          </span>
          <p className="w-40 h-1 bg-auxiliary-dark-gray" />
        </div>
      )}
    </InView>
  );
};

export default InfiniteScroll;
